import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import { ImageTypes } from '~/shared/constants/productGrid';
import { plusSizes } from '~/shared/constants/productSizes';
import { getIsPlusProfile } from '~/shared/utils/getIsPlusProfile';
import { getProfileSizeFiltersFromState } from '~/shared/utils/getProfileSizeFiltersFromState';
import {
  useProductBrowser,
  useProductDetailContext,
} from '~/techstyle-shared/react-components';

const IMAGE_TYPES = new Set(Object.values(ImageTypes));

export default function useProductImageType(imageType) {
  const profileSizes = useSelector(getProfileSizeFiltersFromState);

  const isPlusProfile = useMemo(
    () => (profileSizes ? getIsPlusProfile(profileSizes) : false),
    [profileSizes]
  );
  const productDetailCtx = useProductDetailContext();
  const productBrowserCtx = useProductBrowser();

  return useMemo(() => {
    if (imageType && IMAGE_TYPES.has(imageType)) {
      return imageType;
    }

    if (productBrowserCtx) {
      const { filters } = productBrowserCtx;
      // Check whether any of the currently filtered sizes are in plusSizes
      const plusSizeCurrentlyFiltered = filters.aggregations.some(
        (aggregation) =>
          aggregation.field.startsWith('size_') &&
          filters.aggregationFilter[aggregation.field]?.some((size) =>
            plusSizes.has(size)
          )
      );

      // Check whether any of the currently filtered sizes aren't in plusSizes
      const nonPlusSizeCurrentlyFiltered = filters.aggregations.some(
        (aggregation) =>
          aggregation.field.startsWith('size_') &&
          filters.aggregationFilter[aggregation.field]?.some(
            (size) => !plusSizes.has(size)
          )
      );

      // If the user has filtered by both plus sizes AND non-plus sizes
      // Or if user has no filtered by nothing at all
      // then fallback to their profile sizing
      if (
        (plusSizeCurrentlyFiltered && nonPlusSizeCurrentlyFiltered) ||
        (!plusSizeCurrentlyFiltered && !nonPlusSizeCurrentlyFiltered)
      ) {
        return isPlusProfile ? ImageTypes.Plus : ImageTypes.Default;
      }

      return plusSizeCurrentlyFiltered ? ImageTypes.Plus : ImageTypes.Default;
    }

    if (productDetailCtx) {
      const { isPlusSizeSelected } = productDetailCtx;
      return isPlusSizeSelected ? ImageTypes.Plus : ImageTypes.Default;
    }
  }, [productDetailCtx, productBrowserCtx, isPlusProfile, imageType]);
}
